/**
 * FeatureGate Component
 *
 * Schuetzt Routen basierend auf Feature-Flags.
 * Zeigt einen Hinweis fuer Beta-Features oder verweigerten Zugang.
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, FlaskConical, Lock } from 'lucide-react';

interface FeatureGateProps {
  featureKey: string;
  children: ReactNode;
  fallbackPath?: string;
}

export function FeatureGate({
  featureKey,
  children,
  fallbackPath = '/dashboard',
}: FeatureGateProps) {
  const { user, isAuthenticated, originalRole } = useAuth();
  const { hasAccess, isLoading, isBetaFeature, reason } = useFeatureAccess(featureKey);

  // Nicht eingeloggt -> durchlassen (SEO, öffentliche Vorschau)
  // Feature-Gate greift nur für authentifizierte User
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Admins haben immer Zugang (aber NICHT im Demo-Modus - da entscheidet Feature-Access)
  const isDemoActive = originalRole === 'admin' && user?.role !== 'admin';
  if (user?.role === 'admin' && !isDemoActive) {
    return <>{children}</>;
  }

  // Ladeindikator
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  // Kein Zugang
  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        {isBetaFeature ? (
          <>
            <div className="w-20 h-20 rounded-full bg-sky-500/10 flex items-center justify-center mb-6">
              <FlaskConical className="w-10 h-10 text-sky-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Beta-Feature
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mb-4">
              Dieses Feature ist derzeit nur für Beta-Tester verfügbar.
            </p>
            {reason && (
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {reason}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-gray-500/10 flex items-center justify-center mb-6">
              <Lock className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Zugriff verweigert
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md">
              {reason || 'Sie haben keinen Zugriff auf diese Funktion.'}
            </p>
          </>
        )}
      </div>
    );
  }

  // Zugang gewaehrt
  return <>{children}</>;
}

export default FeatureGate;
